'use client';

import { Box, Container, Divider, Text, Title, TypographyStylesProvider } from '@mantine/core'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import formatDate from '@/utils/format-date'
import type { BlogPost } from '@/services/blogService'
import UnderlineLink from '@/components/UnderlineLink';

const PageTitle = dynamic(() => import('@/components/PageTitle'), { ssr: false })
const HelpChangeSection = dynamic(() => import('@/components/HelpChangeSection'), { ssr: false })
const SiteHeader = dynamic(() => import('@/components/SiteHeader'), { ssr: false })
const SiteFooter = dynamic(() => import('@/components/SiteFooter'), { ssr: false })
const Button = dynamic(() => import('@/components/Button'), { ssr: false })
const ShareButtons = dynamic(() => import('@/components/ShareButtons'), { ssr: false })

interface ResourcePostClientProps {
    post: BlogPost
}

export default function ResourcePostClient({ post }: ResourcePostClientProps) {
    console.log('post', post);
    const hasAuthor = post.author?.trim().length > 0;
    const hasDate = post.date?.trim().length > 0;
    return (
        <Box>
            <SiteHeader scheme="dark" />

            <main>
                <PageTitle
                    subtitle={hasDate && formatDate(post.date)}
                    title={<Title mt={30} fz={42} fw={700} maw={800}>{post.title}</Title>}
                    tagline={hasAuthor && (<Text c="dark.2" fz={18} mt={30}>By {post.author}</Text>)}
                    pt="25vh"
                    pb="15vh"
                    bg="gray.0"
                    style={{ textAlign: 'center' }}
                />

                <Box p={100} pb={0}>
                    <Button href="/resources" variant="outline" hasArrow reverseArrow>
                        Back to all resources
                    </Button>
                </Box>
                <UnderlineLink href="/resources" aria-label="Resources" hidden>Resources</UnderlineLink>

                <Container size="sm">
                    <div className="post-content">
                        {post.image && (
                            <Box mb={30}>
                                <img
                                    src={post.image}
                                    alt={post.title}
                                    style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
                                />
                            </Box>
                        )}

                        <TypographyStylesProvider>
                            <div dangerouslySetInnerHTML={{ __html: post?.content }} />
                        </TypographyStylesProvider>

                        {/* Video Section - moved to bottom for best UX */}
                        {post.videos && post.videos.length > 0 && (
                            <Box mb={40} mt={40}>
                                <Title order={3} mb={20} style={{ textAlign: 'center', color: '#228be6' }}>Featured Video{post.videos.length > 1 ? 's' : ''}</Title>
                                <Box style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '2rem',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                    {post.videos.map((video, idx) => (
                                        <Box key={video.videoId || idx} style={{
                                            width: '100%',
                                            maxWidth: 700,
                                            boxShadow: '0 4px 24px rgba(34,139,230,0.10)',
                                            borderRadius: 12,
                                            overflow: 'hidden',
                                            background: '#000',
                                            margin: '0 auto',
                                            position: 'relative',
                                        }}>
                                            <iframe
                                                width="100%"
                                                height="400"
                                                src={`https://www.youtube.com/embed/${video.videoId}`}
                                                title={post.title + ' video'}
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                                style={{ display: 'block', width: '100%', border: 'none' }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        <Divider label="Share" labelPosition="left" mt={60} mb={20} />
                        <ShareButtons />
                    </div>
                </Container>

                <HelpChangeSection />
                <SiteFooter />
            </main>
        </Box>
    )
}
